/**
 * Demand Estimation Module
 *
 * Estimates annual heat demand (space heating + DHW) based on property characteristics,
 * occupancy, and retrofit status. Provides confidence scoring and reasoning.
 *
 * Precedence (best → worst):
 * 1. Actual annual kWh (if user provides it)
 * 2. Actual annual £ + tariff p/kWh (optional)
 * 3. Modelled demand from property + retrofit + occupancy + water usage
 * 4. Conservative fallback (only if too much is unknown)
 */

import type { SystemRecInput } from '../types';

// ==================== TYPES ====================

export interface DemandEstimate {
  delivered: {
    spaceHeatKwh: number;
    dhwKwh: number;
    totalHeatKwh: number;
  };
  confidence: "low" | "medium" | "high";
  reasons: string[];
  usedActual: boolean;
  method: "actual_usage" | "modelled" | "fallback";
}

// ==================== CONSTANTS ====================

/**
 * Base annual space heat demand by property type (kWh/yr)
 * UK typical values for average insulation
 */
const BASE_SPACE_HEAT_BY_TYPE: Record<string, number> = {
  "flat/bungalow": 7200,
  "flat": 7200,
  "bungalow": 7200,
  "maisonette": 8500,
  "terraced": 8800,
  "end_terrace": 9500,
  "mid_terrace": 8500,
  "semi": 10000,
  "semi_detached": 10000,
  "semi_detached_bungalow": 8500,
  "detached": 12800,
  "unknown": 9600, // Default fallback
};

/**
 * Age band multipliers for heat loss
 * Newer = better fabric efficiency
 */
const AGE_BAND_MULTIPLIER: Record<string, number> = {
  "pre_1919": 1.40,      // Very poor fabric, high heat loss
  "1919_1944": 1.30,     // Poor fabric
  "1945_1964": 1.20,     // Below average fabric
  "1965_1980": 1.10,     // Average fabric
  "1981_2000": 1.00,     // Baseline (modern building regs)
  "2001_2010": 0.85,     // Good fabric
  "2011_plus": 0.70,     // Very good fabric (Part L 2010/2013)
  "unknown": 1.00,       // Assume modern baseline
};

/**
 * Insulation level multipliers
 */
const INSULATION_MULTIPLIER: Record<string, number> = {
  "poor": 1.25,          // Poor insulation
  "medium": 1.00,        // Average insulation
  "high": 0.75,          // Good insulation
  "unknown": 1.00,       // Assume average
};

/**
 * Draught proofing impact multipliers
 */
const DRAUGHT_PROOFING_MULTIPLIER: Record<string, number> = {
  "none": 1.15,          // No draught proofing
  "partial": 1.08,       // Some draught proofing
  "some": 1.08,          // Some draught proofing
  "basic": 1.05,         // Basic draught proofing
  "good": 1.00,          // Good draught proofing
  "modern": 0.95,        // Modern draught proofing
  "unknown": 1.05,       // Assume some issues
  "not_sure": 1.05,      // Assume some issues
};

/**
 * Occupancy timing impact on space heating
 */
const OCCUPANCY_TIMING_MULTIPLIER: Record<string, number> = {
  "evenings_weekends": 0.85,   // Home less, lower demand
  "mornings_evenings": 1.00,   // Typical pattern
  "most_of_day": 1.15,         // Home more, higher demand
  "variable": 1.00,            // Average
  "unknown": 1.00,             // Assume typical
};

/**
 * Base DHW demand by hot water usage level (kWh/yr)
 * UK typical values
 */
const BASE_DHW_BY_DEMAND: Record<string, number> = {
  "low": 1800,           // Low usage
  "medium": 2400,        // Typical usage
  "high": 3200,          // High usage
};

/**
 * DHW adjustment per occupant beyond baseline
 * Baseline is 2-3 occupants
 */
const DHW_PER_OCCUPANT = 600; // kWh/yr per additional person

/**
 * DHW adjustment per draw per day
 * Typical is ~20-30 draws/day for a household
 */
const DHW_PER_DRAW = 50; // kWh/yr per 10 draws/day above 25

// ==================== MAIN FUNCTION ====================

/**
 * Estimate annual heat demand from inputs
 *
 * @param input - System recommendation input
 * @returns DemandEstimate with delivered heat, confidence, and reasoning
 */
export function estimateDemand(input: SystemRecInput): DemandEstimate {
  const reasons: string[] = [];

  // Priority 1: Check for actual usage override
  const actualUsage = input.actualAnnualHeatKwh;
  if (actualUsage && actualUsage > 0) {
    // User provided actual usage - use it directly
    const split = estimateHeatSplit(actualUsage, input);
    reasons.push(`Using actual annual usage: ${actualUsage.toLocaleString()} kWh/yr`);
    return {
      delivered: {
        spaceHeatKwh: split.spaceHeat,
        dhwKwh: split.dhw,
        totalHeatKwh: actualUsage,
      },
      confidence: "high",
      reasons,
      usedActual: true,
      method: "actual_usage",
    };
  }

  // Priority 2: Model from property characteristics
  const spaceHeat = estimateSpaceHeat(input, reasons);
  const dhw = estimateDHW(input, reasons);
  const total = spaceHeat + dhw;

  // Determine confidence based on available data
  const confidence = determineConfidence(input);

  return {
    delivered: {
      spaceHeatKwh: Math.round(spaceHeat),
      dhwKwh: Math.round(dhw),
      totalHeatKwh: Math.round(total),
    },
    confidence,
    reasons,
    usedActual: false,
    method: total > 0 ? "modelled" : "fallback",
  };
}

// ==================== SPACE HEAT ESTIMATION ====================

/**
 * Estimate space heat demand from property characteristics
 */
function estimateSpaceHeat(input: SystemRecInput, reasons: string[]): number {
  const propertyType = normalizePropertyType(input.propertyType);
  const ageBand = input.propertyAgeBand || "unknown";
  const insulationLevel = input.insulationLevel || "unknown";
  const draughtProofing = input.retrofit?.draughtProofing || "unknown";
  const occupancyTiming = input.occupancyTiming || "unknown";
  const roomsStruggle = input.emitters?.roomsStruggleToHeat === "yes";

  // Start with base demand for property type
  let spaceHeat = BASE_SPACE_HEAT_BY_TYPE[propertyType] || BASE_SPACE_HEAT_BY_TYPE["unknown"];
  const originalBase = spaceHeat;

  // Apply age band multiplier (fabric efficiency)
  const ageMult = AGE_BAND_MULTIPLIER[ageBand];
  spaceHeat *= ageMult;

  // Apply insulation multiplier
  const insulationMult = INSULATION_MULTIPLIER[insulationLevel];
  spaceHeat *= insulationMult;

  // Apply draught proofing multiplier
  const draughtMult = DRAUGHT_PROOFING_MULTIPLIER[draughtProofing];
  spaceHeat *= draughtMult;

  // Apply occupancy timing multiplier
  const occupancyMult = OCCUPANCY_TIMING_MULTIPLIER[occupancyTiming];
  spaceHeat *= occupancyMult;

  // If rooms struggle to heat, add 10% penalty (could indicate undersized system or poor distribution)
  if (roomsStruggle) {
    spaceHeat *= 1.10;
  }

  // Apply bedrooms adjustment (proxy for house size if different from type baseline)
  const bedrooms = input.bedrooms;
  if (bedrooms !== undefined && bedrooms !== 3) {
    // Typical property has 3 bedrooms, adjust for variance
    const bedroomAdjustment = (bedrooms - 3) * 800; // 800 kWh per bedroom difference
    spaceHeat += bedroomAdjustment;
  }

  // Build explanation
  const parts: string[] = [];
  parts.push(`${propertyType} (base: ${originalBase.toLocaleString()} kWh)`);
  
  if (ageBand !== "unknown") {
    parts.push(`${ageBand} (×${ageMult.toFixed(2)})`);
  }
  
  if (insulationLevel !== "unknown") {
    parts.push(`${insulationLevel} insulation (×${insulationMult.toFixed(2)})`);
  }
  
  if (draughtProofing !== "unknown" && draughtProofing !== "not_sure") {
    parts.push(`${draughtProofing} draught proofing (×${draughtMult.toFixed(2)})`);
  }
  
  if (occupancyTiming !== "unknown") {
    parts.push(`${occupancyTiming} occupancy (×${occupancyMult.toFixed(2)})`);
  }
  
  if (roomsStruggle) {
    parts.push(`rooms struggle to heat (+10%)`);
  }
  
  if (bedrooms !== undefined && bedrooms !== 3) {
    parts.push(`${bedrooms} bedrooms`);
  }

  reasons.push(`Space heat: ${Math.round(spaceHeat).toLocaleString()} kWh/yr from ${parts.join(", ")}`);

  return spaceHeat;
}

// ==================== DHW ESTIMATION ====================

/**
 * Estimate DHW demand from occupancy and hot water usage
 */
function estimateDHW(input: SystemRecInput, reasons: string[]): number {
  const hotWaterDemand = input.hotWaterDemand || "medium";
  const occupancy = input.occupancy || 2.5; // UK average
  const drawsPerDay = input.drawsPerDay;

  // Start with base DHW for demand level
  let dhw = BASE_DHW_BY_DEMAND[hotWaterDemand];

  // Adjust for occupancy (baseline is 2-3 people)
  const baselineOccupancy = 2.5;
  if (occupancy > baselineOccupancy) {
    const extraPeople = occupancy - baselineOccupancy;
    dhw += extraPeople * DHW_PER_OCCUPANT;
  } else if (occupancy < baselineOccupancy) {
    const fewerPeople = baselineOccupancy - occupancy;
    dhw -= fewerPeople * DHW_PER_OCCUPANT;
  }

  // Adjust for draws per day if provided
  if (drawsPerDay !== undefined) {
    const typicalDraws = 25;
    if (drawsPerDay > typicalDraws) {
      const extraDraws = Math.floor((drawsPerDay - typicalDraws) / 10);
      dhw += extraDraws * DHW_PER_DRAW;
    }
  }

  // Ensure minimum
  dhw = Math.max(1200, dhw);

  // Build explanation
  const parts: string[] = [];
  parts.push(`${hotWaterDemand} demand (base: ${BASE_DHW_BY_DEMAND[hotWaterDemand].toLocaleString()} kWh)`);
  parts.push(`${occupancy} occupants`);
  
  if (drawsPerDay !== undefined) {
    parts.push(`${drawsPerDay} draws/day`);
  }

  reasons.push(`DHW: ${Math.round(dhw).toLocaleString()} kWh/yr from ${parts.join(", ")}`);

  return dhw;
}

// ==================== HELPER FUNCTIONS ====================

/**
 * Estimate space heat vs DHW split when only total is known
 * Typical UK: 80% space heat, 20% DHW
 */
function estimateHeatSplit(totalHeatKwh: number, input: SystemRecInput): { spaceHeat: number; dhw: number } {
  // Try to be smarter about split based on hot water demand
  const hotWaterDemand = input.hotWaterDemand || "medium";
  
  let dhwRatio = 0.20; // Default 20%
  if (hotWaterDemand === "low") {
    dhwRatio = 0.15;
  } else if (hotWaterDemand === "high") {
    dhwRatio = 0.25;
  }

  const spaceHeatRatio = 1 - dhwRatio;

  return {
    spaceHeat: Math.round(totalHeatKwh * spaceHeatRatio),
    dhw: Math.round(totalHeatKwh * dhwRatio),
  };
}

/**
 * Normalize property type to canonical names
 */
function normalizePropertyType(propertyType?: string): string {
  if (!propertyType) return "unknown";
  
  const normalized = propertyType.toLowerCase().trim();
  
  // Map common variations
  if (normalized.includes("flat") || normalized.includes("bungalow")) {
    if (normalized.includes("flat")) return "flat";
    if (normalized.includes("bungalow")) return "bungalow";
    return "flat/bungalow";
  }
  if (normalized.includes("maisonette")) return "maisonette";
  if (normalized.includes("end") && normalized.includes("terrace")) return "end_terrace";
  if (normalized.includes("mid") && normalized.includes("terrace")) return "mid_terrace";
  if (normalized.includes("terrace")) return "terraced";
  if (normalized.includes("semi")) {
    if (normalized.includes("bungalow")) return "semi_detached_bungalow";
    return "semi";
  }
  if (normalized.includes("detached")) return "detached";
  
  // If no match found, return original
  return propertyType;
}

/**
 * Determine confidence level based on available input data
 */
function determineConfidence(input: SystemRecInput): "low" | "medium" | "high" {
  let score = 0;
  let maxScore = 0;

  // Property type (essential)
  maxScore += 2;
  if (input.propertyType) score += 2;

  // Age band (essential)
  maxScore += 2;
  if (input.propertyAgeBand && input.propertyAgeBand !== "unknown") score += 2;

  // Insulation level (important)
  maxScore += 1;
  if (input.insulationLevel && input.insulationLevel !== "unknown") score += 1;

  // Draught proofing (important)
  maxScore += 1;
  if (input.retrofit?.draughtProofing && input.retrofit.draughtProofing !== "unknown" && input.retrofit.draughtProofing !== "not_sure") {
    score += 1;
  }

  // Hot water demand (important)
  maxScore += 1;
  if (input.hotWaterDemand) score += 1;

  // Occupancy (nice to have)
  maxScore += 1;
  if (input.occupancy) score += 1;

  // Calculate percentage
  const percentage = score / maxScore;

  if (percentage >= 0.8) return "high";
  if (percentage >= 0.5) return "medium";
  return "low";
}
